/* eslint-disable camelcase */
import { useState, useEffect } from "react";
import {
  Button,
  Card,
  TextField,
  TextFieldInput,
  TextFieldLabel,
} from "@hireart/component-library";
import $ from "jquery";
import PropTypes from "prop-types";
import queryString from "query-string";
import { connect } from "react-redux";
import { Link, withRouter } from "react-router-dom";

import { setFlashMessage } from "../actions/setFlashMessage";

import { FormError } from "./FormError";

const ResetPassword = ({
  location,
  history,
  setFlashMessage: dispatchFlash,
}) => {
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [invalidResetUrl, setInvalidResetUrl] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [userRootPath, setUserRootPath] = useState("");
  const [resetPasswordToken, setResetPasswordToken] = useState("");

  useEffect(() => {
    const { reset_password_token } = queryString.parse(location.search);

    if (!reset_password_token) {
      setInvalidResetUrl(true);
      setInitialLoad(false);
      setError("Oops! This isn't a valid password reset link.");
      return;
    }

    $.ajax({
      url: "/users/password/edit",
      type: "GET",
      dataType: "json",
      data: { reset_password_token },
    })
      .then(
        ({
          email: fetchedEmail,
          user_root_path,
          token_valid,
          pending_setup,
        }) => {
          if (pending_setup) {
            dispatchFlash(
              "Please specify your password and other account details."
            );
            history.push(
              `user_registration?reset_password_token=${reset_password_token}`
            );
            return;
          }

          if (token_valid) {
            setError("");
            setInitialLoad(false);
            setInvalidResetUrl(false);
            setEmail(fetchedEmail);
            setUserRootPath(user_root_path);
            setResetPasswordToken(reset_password_token);
          } else {
            setError("This password reset link has expired.");
            setInitialLoad(false);
            setInvalidResetUrl(true);
          }
        }
      )
      .catch(({ responseText }) => {
        const { error: fetchedError } = JSON.parse(responseText);
        setError(fetchedError);
        setInvalidResetUrl(true);
        setInitialLoad(false);
      });
    // Intentionally run once on mount (mirrors componentDidMount) — location.search
    // is stable for the lifetime of this route and we must not re-fetch on re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);

    return $.ajax({
      url: "/users/password",
      type: "PUT",
      dataType: "json",
      data: {
        user: {
          email,
          password,
          password_confirmation: passwordConfirmation,
          reset_password_token: resetPasswordToken,
        },
      },
    })
      .then(() => {
        setLoading(false);
        setResetSuccess(true);
        setError("");
        window.location.href = userRootPath;
      })
      .catch(({ responseText }) => {
        const errorList = [];
        const {
          errors: {
            password: passwordErrors,
            reset_password_token: tokenErrors,
          },
        } = JSON.parse(responseText);

        if (passwordErrors) {
          passwordErrors.forEach((err) =>
            err.match(/passwords/gi)
              ? errorList.push(err)
              : errorList.push(`Password ${err}`)
          );
        }
        if (tokenErrors) {
          tokenErrors.forEach((err) =>
            errorList.push(`Reset password token ${err}`)
          );
        }

        setLoading(false);
        setShowError(true);
        setError(errorList);
      });
  };

  if (initialLoad) {
    return (
      <Card className="rounded-0 border-0 px-24 py-32 sm:rounded-16 sm:border-1">
        <p>Loading...</p>
      </Card>
    );
  }

  if (invalidResetUrl) {
    return (
      <Card className="rounded-0 border-0 px-24 py-32 sm:rounded-16 sm:border-1">
        <h2 className="headline-1 mb-24 text-center">Reset your password</h2>
        <p>
          {error}&nbsp;Please click the link below to reset your password again.
        </p>
        <p className="mt-16">
          <Link to="/forgot_password">Reset my password</Link>
        </p>
      </Card>
    );
  }

  return (
    <Card className="rounded-0 border-0 px-24 py-32 sm:rounded-16 sm:border-1">
      <h2 className="headline-1 mb-24 text-center">
        {resetSuccess ? (
          <>
            Success! <br />
            Your password has been reset.
          </>
        ) : (
          "Reset your password"
        )}
      </h2>

      <FormError error={error} />

      {!resetSuccess && <p>Please create a new password for {email}</p>}

      {!resetSuccess ? (
        <form onSubmit={handleSubmit}>
          <div className="my-16">
            <TextField hasError={error.length > 0 && showError}>
              <TextFieldLabel htmlFor="password" isUnstyled>
                New Password
              </TextFieldLabel>
              <TextFieldInput
                autoFocus
                id="password"
                name="password"
                onChange={(e) => {
                  setPassword(e.target.value);
                  setShowError(false);
                }}
                placeholder="New Password (at least 8 characters, 1 Uppercase, 1 Lowercase, 1 Number, 1 Special Character)"
                size="md"
                type="password"
                value={password}
              />
            </TextField>
          </div>

          <div className="my-16">
            <TextField hasError={error.length > 0 && showError}>
              <TextFieldLabel htmlFor="password_confirmation" isUnstyled>
                Confirm new password
              </TextFieldLabel>
              <TextFieldInput
                id="password_confirmation"
                name="password_confirmation"
                onChange={(e) => {
                  setPasswordConfirmation(e.target.value);
                  setShowError(false);
                }}
                placeholder="Confirm new password"
                size="md"
                type="password"
                value={passwordConfirmation}
              />
            </TextField>
          </div>

          <Button
            isLoading={loading}
            type="submit"
            isFullWidth
            isDisabled={
              password.trim().length === 0 ||
              passwordConfirmation.trim().length === 0 ||
              loading
            }
          >
            Reset my password
          </Button>
        </form>
      ) : (
        <p>Taking you to your dashboard...</p>
      )}
    </Card>
  );
};

ResetPassword.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  setFlashMessage: PropTypes.func.isRequired,
};

const ConnectedResetPassword = withRouter(
  connect(null, { setFlashMessage })(ResetPassword)
);

export { ConnectedResetPassword as ResetPassword };
